/**
 * @file Description.tsx
 * Displays descriptive information about a given program: grade info, location, etc.
 */
import React, { FC, useMemo, useState } from 'react';
import { Modal, Button } from 'antd';
import { IMoreInfoDescriptionProps } from './types';
import dayjs from 'dayjs';
import { sanitizeHtml } from '../../../../utils/sanitizeHtml';

/**
 * Renders textual and descriptive information for a program.
 *
 * @param {IMoreInfoDescriptionProps} props - The props for the Description component.
 */
const Description: FC<IMoreInfoDescriptionProps> = ({
  program,
  gradeStatement,
  locationName,
}) => {
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  const content = useMemo(() => {
    return (
      <>
        <p>{gradeStatement}</p>
        <p>{program?.generalTime}</p>
        <p>
          {dayjs(program?.startDate).format('MM/DD/YYYY')} -{' '}
          {dayjs(program?.endDate).format('MM/DD/YYYY')}
        </p>
        <p>Location: {locationName}</p>

        <hr className="mt-3 mb-3" />

        <Button
          onClick={() => setIsDescriptionOpen(true)}
          type="primary"
          className="btn-sac"
        >
          View Program Description
        </Button>

        <Modal
          title="Program Description"
          open={isDescriptionOpen}
          onCancel={() => setIsDescriptionOpen(false)}
          footer={null}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(program?.description),
            }}
          />
        </Modal>

        <hr className="mt-3 mb-3" />

        <p>
          <b>Registration Requirements</b>
        </p>

        <p>
          Students must be {gradeStatement}
          {program?.locationRequired && (
            <>
              <br />
              Students must also be enrolled in a school that corresponds with
              the STAR site at {locationName}.
            </>
          )}
        </p>

        <p>
          If you do not see your desired schedule, please email your STAR Site
          to inquire about wait list options. Contact information for your site
          can be found{' '}
          <a
            href="https://starsacramento.org/contact"
            target="_blank"
            rel="noreferrer"
          >
            HERE
          </a>
          .
        </p>

        <hr className="mt-3 mb-3" />
      </>
    );
  }, [program, gradeStatement, locationName, isDescriptionOpen]);

  return content;
};

export default Description;
